import React from 'react'
import { useState, useMemo } from 'react'
import { motion } from 'framer-motion'
import { 
  Container, 
  Wrapper, 
  Title, 
  Desc, 
  CardContainer, 
  ToggleButtonGroup, 
  ToggleButton, 
  Divider,
  SearchContainer,
  SearchInput,
  SearchIcon,
  TechFilterContainer,
  TechTag,
  ProjectStats,
  StatItem,
  StatValue,
  StatLabel,
  FilterActions,
  ClearFilterButton,
  EmptyState,
  EmptyTitle,
  EmptyDesc,
} from './ProjectsStyle'
import ProjectCard from '../Cards/ProjectCards'
import { projects } from '../../data/constants'
import { fadeInUp, staggerContainer, staggerItem } from '../../utils/animations'
import { FaSearch } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'

const Projects = ({openModal, setOpenModal}) => {
  const { t } = useTranslation();
  const [toggle, setToggle] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTech, setSelectedTech] = useState(null);
  const [showAllTech, setShowAllTech] = useState(false);

  const allTechnologies = useMemo(() => {
    const techSet = new Set();
    projects.forEach(project => {
      if (project.tags && Array.isArray(project.tags)) {
        project.tags.forEach(tag => {
          if (typeof tag === 'string') {
            tag.split(',').forEach((tech) => techSet.add(tech.trim()));
          }
        });
      }
    });
    return Array.from(techSet).sort();
  }, []);

  const visibleTechnologies = showAllTech
    ? allTechnologies
    : allTechnologies.slice(0, 10);

  const hasActiveFilters =
    toggle !== 'all' ||
    selectedTech !== null ||
    searchQuery.trim().length > 0;

  const clearFilters = () => {
    setToggle('all');
    setSearchQuery('');
    setSelectedTech(null);
  };

  const renderToggleButton = (value, label) => (
    toggle === value 
      ? <ToggleButton active value={value} onClick={() => setToggle(value)}>{label}</ToggleButton>
      : <ToggleButton value={value} onClick={() => setToggle(value)}>{label}</ToggleButton>
  );

  const filteredProjects = useMemo(() => {
    let filtered = projects;

    if (toggle !== 'all') {
      filtered = filtered.filter((item) => item.category === toggle);
    }

    if (selectedTech) {
      filtered = filtered.filter((project) => {
        if (!project.tags) return false;
        return project.tags.some(tag => {
          const tagStr = typeof tag === 'string' ? tag : '';
          return tagStr.toLowerCase().includes(selectedTech.toLowerCase());
        });
      });
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((project) => {
        const titleMatch = project.title?.toLowerCase().includes(query);
        const descMatch = project.description?.toLowerCase().includes(query);
        const tagMatch = project.tags?.some(tag => {
          const tagStr = typeof tag === 'string' ? tag : '';
          return tagStr.toLowerCase().includes(query);
        });
        return titleMatch || descMatch || tagMatch;
      });
    }

    return filtered;
  }, [toggle, searchQuery, selectedTech]);

  const projectStats = useMemo(() => {
    return {
      total: projects.length,
      showing: filteredProjects.length,
      technologies: allTechnologies.length,
      categories: new Set(projects.map(p => p.category)).size
    };
  }, [filteredProjects, allTechnologies]);

  return (
    <Container id="projects">
      <Wrapper>
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
        >
          <Title>{t('projects.title')}</Title>
        </motion.div>
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
          transition={{ delay: 0.1 }}
        >
          <Desc>
            {t('projects.description')}
          </Desc>
        </motion.div>
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
          transition={{ delay: 0.2 }}
        >
          <SearchContainer>
            <SearchIcon aria-hidden="true">
              <FaSearch />
            </SearchIcon>
            <SearchInput
              type="search"
              placeholder={t('projects.searchPlaceholder')}
              aria-label={t('projects.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchContainer>
        </motion.div>

        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
          transition={{ delay: 0.25 }}
        >
          <ProjectStats>
            <StatItem>
              <StatValue>{projectStats.total}</StatValue>
              <StatLabel>{t('projects.stats.total')}</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>{projectStats.showing}</StatValue>
              <StatLabel>{t('projects.stats.showing')}</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>{projectStats.technologies}</StatValue>
              <StatLabel>{t('projects.stats.technologies')}</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>{projectStats.categories}</StatValue>
              <StatLabel>{t('projects.stats.categories')}</StatLabel>
            </StatItem>
          </ProjectStats>
        </motion.div>

        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
          transition={{ delay: 0.3 }}
        >
          <ToggleButtonGroup>
            {renderToggleButton('all', t('projects.filters.all'))}
            <Divider />
            {renderToggleButton('web app', t('projects.filters.webApps'))}
            <Divider />
            {renderToggleButton('machine learning', t('projects.filters.machineLearning'))}
          </ToggleButtonGroup>
        </motion.div>

        {allTechnologies.length > 0 && (
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            transition={{ delay: 0.35 }}
          >
            {hasActiveFilters && (
              <FilterActions>
                <ClearFilterButton type="button" onClick={clearFilters}>
                  {t('projects.filters.clearFilters')}
                </ClearFilterButton>
              </FilterActions>
            )}
            <TechFilterContainer>
              <TechTag
                type="button"
                active={selectedTech === null}
                onClick={() => setSelectedTech(null)}
              >
                {t('projects.filters.allTech')}
              </TechTag>
              {visibleTechnologies.map((tech) => (
                <TechTag
                  key={tech}
                  type="button"
                  active={selectedTech === tech}
                  onClick={() => setSelectedTech(selectedTech === tech ? null : tech)}
                >
                  {tech}
                </TechTag>
              ))}
              {allTechnologies.length > 10 && (
                <TechTag
                  type="button"
                  active={false}
                  onClick={() => setShowAllTech((v) => !v)}
                >
                  {showAllTech
                    ? t('projects.filters.showLessTech')
                    : t('projects.filters.showAllTech')}
                </TechTag>
              )}
            </TechFilterContainer>
          </motion.div>
        )}
        {filteredProjects.length > 0 ? (
          <CardContainer
            as={motion.div}
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            key={`${toggle}-${searchQuery}-${selectedTech}`}
          >
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                variants={staggerItem}
                initial="initial"
                animate="animate"
                transition={{ delay: index * 0.1 }}
              >
                <ProjectCard 
                  project={project} 
                  openModal={openModal} 
                  setOpenModal={setOpenModal}
                />
              </motion.div>
            ))}
          </CardContainer>
        ) : (
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <EmptyState>
              <div style={{ fontSize: '48px', marginBottom: '16px' }} aria-hidden="true">🔍</div>
              <EmptyTitle>{t('projects.empty.title')}</EmptyTitle>
              <EmptyDesc>{t('projects.empty.description')}</EmptyDesc>
              {hasActiveFilters && (
                <ClearFilterButton
                  type="button"
                  onClick={clearFilters}
                  style={{ marginTop: '16px' }}
                >
                  {t('projects.filters.clearFilters')}
                </ClearFilterButton>
              )}
            </EmptyState>
          </motion.div>
        )}
      </Wrapper>
    </Container>
  )
}

export default Projects
